// client/src/pages/students/StudentCourseManagement.jsx

import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ContentListModal, LessonModal, AssessmentModal,  
    AnnouncementDetailModal, ConversationModal  } from '../../modals';
import ProfileHeader from '../../components/headers/profileHeader';
import SharedHeader from '../../components/headers/sharedHeader1';
import showNotification from '../../utilities/showNotification';
import ProfIXChatbot from '../../components/chatBot/ProfIXChatbot';
import BackButton from '../../components/customs/backButton';
import { GlobalContext } from '../../context/provider';
import { CourseDomains } from '../../types/courseDomains';
import useStudent from '../../hooks/useStudent';
import useCourse from '../../hooks/useCourse';
import useCategory from '../../hooks/useCategory';
import useBlobStorage from '../../hooks/useBlobStorage';
import useMessage from '../../hooks/useMessage';
import useAnnouncement from '../../hooks/useAnnouncement';
import useSimulator from '../../hooks/useSimulator';
import useVisualCircuit from '../../hooks/useVisualCircuit';

import '../../styles/globalstyles.css';
import '../../modals/admin/AdminCourseDetailModal.css';
import './studentCourseManagement.css';

const StudentCourseManagement = () => {
    const { courseId } = useParams();
    const navigate = useNavigate();
    // const location = useLocation();
    const { authState: { authData } } = useContext(GlobalContext);
    
    const { 
        getCapstoneProgress,
        getLabProgress,
        getExamProgress,
        getQuizProgress,
        getCourseGrade,
        getPaymentHistory,      
        getSubscriptions,       
        cancelSubscription,     
        refundPayment,          
        refundToCredit,
    } = useStudent();
    const { getCourseById } = useCourse();
    const { getCategoryByTitle } = useCategory();
    const { triggerDownload } = useBlobStorage();
    const { getMessagesAndMarkSeen, sendMessage, getUnreadCount } = useMessage();
    const { getStudentAnnouncements } = useAnnouncement();
    const { getSimulatorBySessionId } = useSimulator();

    const { courseCircuits, getCircuitsByCourse } = useVisualCircuit();
    const [activeTab, setActiveTab] = useState('content');

    const [isLoading, setIsLoading] = useState(true);
    const [courseInfo, setCourseInfo] = useState(null);
    const [tags, setTags] = useState([]);
    const [courseProgress, setCourseProgress] = useState(null);
    const [courseGrade, setCourseGrade] = useState(null);

    // course circuit
    const [myCircuitsCount, setMyCircuitsCount] = useState(0);
    const [domainName, setDomainName] = useState('');

    // Modal states
    const [showContentModal, setShowContentModal] = useState(false);
    const [selectedContent, setSelectedContent] = useState(null);
    const [selectedContentType, setSelectedContentType] = useState(null);
    const [selectedItemId, setSelectedItemId] = useState(null);
    const [selectedListType, setSelectedListType] = useState(null);
    const [assessmentPhase, setAssessmentPhase] = useState('overview'); // 'overview' | 'attempt' | 'results'
    
    // Submission states
    const [showListModal, setShowListModal] = useState(false);
    const [labSimulators, setLabSimulators] = useState({});   // { [questionId]: { simulator, circuit } }
    const [iotModalOpen,  setLabModalOpen]  = useState({});   // { [questionId]: true/false }
    
    // Message states
    const [conversationMessages, setConversationMessages] = useState([]);
    const [instructorInfo, setInstructorInfo] = useState(null);
    
    // Announcement Stats
    const [studentAnnouncements, setStudentAnnouncements] = useState([]);
    const [loadingAnnouncements, setLoadingAnnouncements] = useState(false);
    const [messageText, setMessageText] = useState('');
    const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
    const [announcementModalVisible, setAnnouncementModalVisible] = useState(false);
    
    const [gradingCollapsed, setGradingCollapsed] = useState({});

    const [statsAssignments, setStatsAssignments] = useState({
        labsPending: 0, labsSubmitted: 0,
        quizzesPending: 0, quizzesSubmitted: 0,
        examsPending: 0, examsSubmitted: 0,
        capstonesPending: 0, capstonesSubmitted: 0,
        allPending: 0, allSubmitted: 0,
    });

    const [hasTextbook, setHasTextbook] = useState(false);

    const specialCasing = {
        iot: 'IoT',
        ai: 'AI',
        mcq: 'MCQ'
    };

    // useEffect(() => {
    //     if (location.state?.activeTab) {
    //         setActiveTab(location.state.activeTab);
    //     }
    // }, [location.state]);

    useEffect(() => {
        loadPage();
    }, [courseId]);

    const loadPage = async () => {
        setIsLoading(true);
        try {
            // Load course info
            const courseResponse = await getCourseById(courseId);
            setDomainName(courseResponse.data.category);
            // if (courseId && authData?.id) {
            //     await getCircuitsByCourse(courseId, authData.id);
            // }

            if (courseId && authData?.id) {
                const result = await getCircuitsByCourse(courseId, authData.id);
                setMyCircuitsCount(result?.data?.length || 0);
            }

            if (courseResponse.success) {
                setCourseInfo(courseResponse.data);

                if (courseResponse?.data?.category) {
                const response = await getCategoryByTitle(courseResponse.data.category);
                if (response?.success && response.data?.tags) {
                    setTags(response.data.tags);
                }
                }
                
                // ✅ NEW: Load instructor info from course data
                if (courseResponse.data.instructorId) {
                    const instructor = {
                        loginId: courseResponse.data.instructorId,
                        name: courseResponse.data.instructorName || 'Course Instructor',
                        email: courseResponse.data.instructorEmail || '',
                        imageUrl: courseResponse.data.instructorImageUrl || '/images/default-avatar.png'
                    };
                    setInstructorInfo(instructor);

                    // Load messages with instructor
                    try {
                        const messagesResponse = await getMessagesAndMarkSeen(
                            instructor.loginId,  
                            authData.id,         
                            { page: 1, limit: 50 }
                        );
                        
                        if (messagesResponse.success) {
                            setConversationMessages(messagesResponse.messages || []);
                        }
                    } catch (error) {
                        console.error('Failed to load messages:', error);
                    }
                } else {
                    console.warn('⚠️ No instructor assigned to this course');
                    setInstructorInfo({
                        loginId: null,
                        name: 'No Instructor Assigned',
                        email: '',
                        imageUrl: '/images/default-avatar.png'
                    });
                }
            }

            // Load unread count
            try {
                await getUnreadCount(authData.id);
            } catch (error) {
                console.error('Failed to load unread count:', error);
            }

            // Load announcements
            try {
                await loadStudentAnnouncements();
            } catch (error) {
                console.error('Failed to load announcements:', error);
            }

            // Use the 4 separate APIs to get all progress
            const labProgressRes = await getLabProgress(authData.id, courseId);
            const quizProgressRes = await getQuizProgress(authData.id, courseId);
            const examProgressRes = await getExamProgress(authData.id, courseId);
            const capstoneProgressRes = await getCapstoneProgress(authData.id, courseId);

            // Combine all progress data into the structure your component expects
            const combinedProgress = {
                labProgress: labProgressRes.success ? labProgressRes.data : { courseId, labs: [] },
                quizProgress: quizProgressRes.success ? quizProgressRes.data : { courseId, quizzes: [] },
                examProgress: examProgressRes.success ? examProgressRes.data : { courseId, exams: [] },
                capstoneProjects: capstoneProgressRes.success && capstoneProgressRes.data 
                    ? capstoneProgressRes.data 
                    : { courseId, capstones: [] },
                totalLabs: labProgressRes.data?.labs?.length || 0,
                totalQuizzes: quizProgressRes.data?.quizzes?.length || 0,
                totalExams: examProgressRes.data?.exams?.length || 0,
                totalCapstones: capstoneProgressRes.data?.capstones?.length || 0
            };
            setCourseProgress(combinedProgress);

            // ✅ Calculate statistics using backend averages
            let labsTotal = 0;
            let labsAverage = 0;
            let labsPoints = 0, labsMaxPoints = 0, labsCount = 0;
            combinedProgress.labProgress?.labs?.forEach(lab => {
                labsTotal++;
                const courseItem = combinedProgress?.labProgress?.labs?.find(l => l.labId === lab.labId);
                const maxGrade = courseInfo?.labs?.find(l => l.labId === lab.labId)?.maxGrade || 1;
                if ((lab.bestGrade || 0) > 0) {
                    labsPoints += lab.bestGrade;
                    labsMaxPoints += maxGrade;
                    labsCount++;
                }
            });
            const avgLabs = labsMaxPoints > 0 ? (labsPoints / labsMaxPoints) * 100 : 0;

            let quizzesTotal = 0;
            let quizzesAverage = 0;
            let quizzesCount = 0;
            combinedProgress.quizProgress?.quizzes?.forEach(quiz => {
                quizzesTotal++;
                if (quiz.bestGrade !== undefined) {
                    quizzesAverage += quiz.bestGrade;
                    quizzesCount++;
                }
            });

            let examsTotal = 0;
            let examsAverage = 0;
            let examsCount = 0;
            combinedProgress.examProgress?.exams?.forEach(exam => {
                examsTotal++;
                if ((exam.finalGrade || 0) > 0) {
                    examsAverage += exam.finalGrade;
                    examsCount++;
                }
            });

            let capstonesTotal = 0;
            let capstonesAverage = 0;
            let capstonesCount = 0;
            combinedProgress.capstoneProjects?.capstones?.forEach(capstone => {
                capstonesTotal++;
                if ((capstone.bestGrade || capstone.finalGrade || 0) > 0) {
                    capstonesAverage += (capstone.bestGrade || capstone.finalGrade || 0);
                    capstonesCount++;
                }
            });

            // Calculate overall averages
            const avgQuizzes = quizzesCount > 0 ? quizzesAverage / quizzesCount : 0;
            const avgExams = examsCount > 0 ? examsAverage / examsCount : 0;
            const avgCapstones = capstonesCount > 0 ? capstonesAverage / capstonesCount : 0;

            

            // Calculate overall student average (across all assignment types)
            const totalGradePoints = labsAverage + quizzesAverage + examsAverage + capstonesAverage;
            const totalItems = labsCount + quizzesCount + examsCount + capstonesCount;
            const overallAverage = totalItems > 0 ? totalGradePoints / totalItems : 0;

            // Count pending assignments
            let pendingCount = 0;

            combinedProgress.labProgress?.labs?.forEach(lab => {
                const lastAttempt = lab.attempts?.[lab.attempts.length - 1];
                if (lastAttempt?.status === 'submitted') pendingCount++;
            });

            combinedProgress.quizProgress?.quizzes?.forEach(quiz => {
                const lastAttempt = quiz.attempts?.[quiz.attempts.length - 1];
                if (lastAttempt?.status === 'submitted') pendingCount++;
            });

            combinedProgress.examProgress?.exams?.forEach(exam => {
                const lastAttempt = exam.attempts?.[exam.attempts.length - 1];
                if (lastAttempt?.status === 'submitted') pendingCount++;
            });

            combinedProgress.capstoneProjects?.capstones?.forEach(capstone => {
                const lastSubmission = capstone.submissions?.[capstone.submissions.length - 1];
                if (lastSubmission?.status === 'submitted') pendingCount++;
            });

            const totalAssignments = labsTotal + quizzesTotal + examsTotal + capstonesTotal;

            setStatsAssignments({
                labsPending: 0,
                labsSubmitted: labsTotal,
                quizzesPending: 0,
                quizzesSubmitted: quizzesTotal,
                examsPending: 0,
                examsSubmitted: examsTotal,
                capstonesPending: 0,
                capstonesSubmitted: capstonesTotal,
                allPending: pendingCount,
                allSubmitted: totalAssignments,
                avgLabs: avgLabs,
                avgQuizzes: avgQuizzes,
                avgExams: avgExams,
                avgCapstones: avgCapstones,
                overallAverage: overallAverage
            });

            // Load grades
            const gradeResponse = await getCourseGrade(authData.id, courseId);
            if (gradeResponse.success) {
                setCourseGrade(gradeResponse.data);
            }

        } catch (error) {
            console.error('Failed to load course data:', error);
            showNotification('Failed to load course data', 'error');
        } finally {
            setIsLoading(false);
        }
    };
 
    useEffect(() => {
        if (!courseInfo?.category) {
            setHasTextbook(false);
            return;
        }
        setHasTextbook(false);
        const url = `/courses/${encodeURIComponent(courseInfo.category)}/textbook.json`;
        fetch(url)
            .then(res => {
                const contentType = res.headers.get('content-type');
                setHasTextbook(res.ok && contentType?.includes('application/json'));
            })
            .catch((err) => {
                console.log('Textbook fetch error:', err);
                setHasTextbook(false);
            });
    }, [courseInfo?.category]);

    function formatType(str) {
    const cleaned = str.replace('_', ' ');
    return cleaned
        .split(' ')
        .map(word => specialCasing[word.toLowerCase()] || word)
        .join(' ');
    }

    const handleSendMessage = async (e) => {
        e.preventDefault();
        
        if (!messageText.trim() || !instructorInfo?.loginId) {
            showNotification('Please enter a message', 'error');
            return;
        }
        
        try {
            const messageData = {
                targetUserId: instructorInfo.loginId,
                targetName: instructorInfo.name,
                title: `Message from ${authData.name}`,
                body: messageText,
                senderUserId: authData.id,
                senderName: authData.name,
                type: 'general',
                priority: 'medium',
                actionRequired: false
            };
            
            const response = await sendMessage(messageData);
            
            if (response.success) {
                showNotification('Message sent successfully', 'success');
                setMessageText('');
                
                // Reload messages to show the new one
                const messagesResponse = await getMessagesAndMarkSeen(
                    authData.id,
                    instructorInfo.loginId,
                    { page: 1, limit: 50 }
                );
                
                if (messagesResponse.success) {
                    setConversationMessages(messagesResponse.messages || []);
                }
            } else {
                showNotification('Failed to send message', 'error');
            }
        } catch (error) {
            console.error('Failed to send message:', error);
            showNotification('An error occurred', 'error');
        }
    };

    const loadStudentAnnouncements = async () => {
        setLoadingAnnouncements(true);
        try {
            const response = await getStudentAnnouncements(authData.id, 1, 50);
            if (response.success) {
                setStudentAnnouncements(response.announcements || []);
            }
        } catch (error) {
            console.error('Failed to load announcements:', error);
        } finally {
            setLoadingAnnouncements(false);
        }
    };

    const closeContentModal = () => {
        setAssessmentPhase('overview');
        setShowContentModal(false);
        setSelectedContentType(null);
        setSelectedItemId(null);
    };

    const openListModal = (contentType) => {
        setSelectedListType(contentType);
        setShowListModal(true);
    };

    const closeListModal = () => {
        setShowListModal(false);
        setSelectedListType(null);
    };

    const openContentModalFromList = async (contentType, itemId, phase = 'overview') => {
        setAssessmentPhase(phase);
        setSelectedContentType(contentType);
        setSelectedItemId(itemId);

        // For results phase, fetch IoT simulator data like instructor page does
        if (phase === 'results' && contentType === 'labs') {
            const lab = courseInfo?.labs?.find(l => l.labId === itemId);
            const labProgress = courseProgress?.labProgress?.labs?.find(l => l.labId === itemId);
            const lastAttempt = labProgress?.attempts?.[labProgress.attempts.length - 1];

            if (lab && lastAttempt) {
                const labSessionsData = {};
                for (const question of lab.questions) {
                    if (question.questionType === 'lab_practical') {
                        const answer = lastAttempt.answers?.find(a => a.questionId === question.questionId);
                        if (answer?.sessionId) {
                            try {
                                const simResponse = await getSimulatorBySessionId(answer.sessionId);
                                if (simResponse.success) {
                                    labSessionsData[question.questionId] = simResponse.data;
                                }
                            } catch (error) {
                                console.error(`Failed to fetch simulator for question ${question.questionId}:`, error);
                            }
                        }
                    }
                }
                setLabSimulators(labSessionsData);
            }
        }

        setShowContentModal(true);
        setShowListModal(false);
    };

    const pluralMap = {
        lab: 'labs',
        exam: 'exams',
        quiz: 'quizzes',
        capstone: 'capstones'
    };

    const handleDownloadAttachment = async (fileId, filename) => {
        try {
            await triggerDownload(fileId, filename, authData.id);
            showNotification('File download started', 'success');
        } catch (error) {
            showNotification('Failed to download file', 'error');
        }
    };

    const handleViewAnnouncement = (announcement) => {
        setSelectedAnnouncement(announcement);
        setAnnouncementModalVisible(true);
    };

    const renderStudentProgressTable = (items, type, icon, title) => {
        if (!items || items.length === 0) {
            return (
            <div className="grading-category">
                <div className="grading-category-header">
                <h3>{icon} {title} (0)</h3>
                </div>
                <p className="no-submissions">No {type} items available</p>
            </div>
            );
        }

        return (
            <div className="grading-category">
                <div className="grading-category-header">
                    <h3>{icon} {title} ({items.length})</h3>
                </div>

                <div className="submissions-table-container">
                    <table className="submissions-table">
                    <thead>
                        <tr>
                        <th>Item</th>
                        <th className="center">Attempts</th>
                        <th className="center">Best Grade</th>
                        <th className="center">Average Grade</th>
                        <th className="center">Status</th>
                        <th className="center">Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item, idx) => {
                        const idField = type === 'lab' ? 'labId' : 
                                        type === 'quiz' ? 'quizId' :
                                        type === 'exam' ? 'examId' : 'capstoneId';
                        const itemId = item[idField];
                        
                        // Get progress for this item
                        let progress = null;
                        let attempts = [];
                        let bestGrade = 0;
                        let avgGrade = 0;
                        let status = 'Not Started';
                        
                        if (type === 'lab') {
                            progress = courseProgress?.labProgress?.labs?.find(l => l.labId === itemId);
                            attempts = progress?.attempts || [];
                            bestGrade = progress?.bestGrade || 0;
                            avgGrade = progress?.averageGrade || 0;
                        } else if (type === 'quiz') {
                            progress = courseProgress?.quizProgress?.quizzes?.find(q => q.quizId === itemId);
                            attempts = progress?.attempts || [];
                            bestGrade = progress?.bestGrade || 0;
                            avgGrade = progress?.averageGrade || 0;
                        } else if (type === 'exam') {
                            progress = courseProgress?.examProgress?.exams?.find(e => e.examId === itemId);
                            attempts = progress?.attempts || [];
                            bestGrade = progress?.finalGrade || 0;
                            avgGrade = progress?.finalGrade || 0;
                        } else if (type === 'capstone') {
                            progress = courseProgress?.capstoneProjects?.capstones?.find(c => c.capstoneId === itemId);
                            const submissions = progress?.submissions || [];
                            attempts = submissions;
                            bestGrade = progress?.bestGrade || 0;
                        }
                        
                        // Determine status
                        if (attempts.length === 0) {
                            status = 'Not Started';
                        } else {
                            const lastAttempt = attempts[attempts.length - 1];
                            if (lastAttempt.status === 'graded') {
                            status = 'Graded';
                            } else if (lastAttempt.status === 'submitted') {
                            status = 'Pending';
                            } else {
                            status = 'In Progress';
                            }
                        }
                        
                        return (
                            <tr key={idx}>
                            <td>
                                <div className="item-info">
                                <strong>{itemId}</strong>
                                <div className="item-title">{item.title}</div>
                                </div>
                            </td>
                            <td className="text-center">
                                <span className="attempts-badge">
                                {attempts.length} / {item.attemptsAllowed || 1 }
                                </span>
                            </td>
                            <td className="text-center">
                                <span className="grade-display">
                                {bestGrade.toFixed(1)} / {item.maxGrade}
                                </span>
                            </td>
                            <td className="text-center">
                                <span className="grade-display">
                                {type !== 'exam' ? avgGrade.toFixed(1) : '-'}
                                </span>
                            </td>
                            <td className="text-center">
                                <span className={`status-badge ${
                                status === 'Graded' ? 'graded' : 
                                status === 'Pending' ? 'pending' : 
                                status === 'In Progress' ? 'in-progress' : 'not-started'
                                }`}>
                                {status === 'Graded' && '✅ Graded'}
                                {status === 'Pending' && '⏳ Pending'}
                                {status === 'In Progress' && '📝 In Progress'}
                                {status === 'Not Started' && '🔒 Not Started'}
                                </span>
                            </td>
                            {status === 'Graded' && (<td className="text-center">
                                <button
                                    onClick={() => openContentModalFromList(pluralMap[type], itemId, 'results')}
                                    className="view-details-btn"
                                    >
                                    👁️ View
                                </button>
                            </td>)}
                            </tr>
                        );
                        })}
                    </tbody>
                    </table>
                </div>
            </div>
        );
    };

    const renderOverallStatistics = () => {
        return (
            <div className="grading-category">
            <div className="grading-category-header">
                <h3>📈 Overall Course Statistics</h3>
            </div>

            <div className="submissions-table-container">
                <table className="submissions-table">
                <thead>
                    <tr>
                    <th>Category</th>
                    <th className="center">Completed</th>
                    <th className="center">Total</th>
                    <th className="center">Average Grade</th>
                    <th className="center">Weight</th>
                    <th className="center">Weighted Score</th>
                    </tr>
                </thead>
                <tbody>
                    {/* Labs Row */}
                    <tr>
                    <td>
                        <div className="student-info">
                        <span className="category-icon">🧪</span>
                        <strong>Labs</strong>
                        </div>
                    </td>
                    <td className="text-center">
                        <span className="progress-fraction">
                        {statsAssignments.labsSubmitted}
                        </span>
                    </td>
                    <td className="text-center">
                        <span className="progress-fraction">
                        {courseInfo?.labs?.length || 0}
                        </span>
                    </td>
                    <td className="text-center">
                        <span className="grade-display">
                        {(() => {
                            const labs = courseProgress?.labProgress?.labs || [];
                            const graded = labs.filter(l => (l.bestGrade || 0) > 0);
                            if (graded.length === 0) return '0.0';
                            const sum = graded.reduce((s, l) => {
                                const maxGrade = courseInfo?.labs?.find(cl => cl.labId === l.labId)?.maxGrade || 1;
                                return s + (l.bestGrade / maxGrade) * 100;
                            }, 0);
                            return (sum / graded.length).toFixed(1);
                        })()}%
                        </span>
                    </td>
                    <td className="text-center">
                        {/* <span className="grade-display">
                            {Math.round((courseInfo?.labs?.reduce((sum, lab) => sum + (lab.gradeWeight || 0), 0) || 0) * 1000) / 10}%
                        </span> */}
                        <span className="grade-display">{(() => {
                            const submittedLabIds = courseProgress?.labProgress?.labs?.map(l => l.labId) || [];
                            const weight = courseInfo?.labs?.filter(l => submittedLabIds.includes(l.labId)).reduce((sum, l) => sum + (l.gradeWeight || 0), 0) || 0;
                            return Math.round(weight * 1000) / 10;
                            })()}%
                        </span>
                    </td>
                    <td className="text-center">
                        <span className="grade-display">{courseGrade?.labsGrade?.toFixed(1) || '0.0'}%</span>
                    </td>
                    </tr>

                    {/* Quizzes Row */}
                    <tr>
                    <td>
                        <div className="student-info">
                        <span className="category-icon">📋</span>
                        <strong>Quizzes</strong>
                        </div>
                    </td>
                    <td className="text-center">
                        <span className="progress-fraction">
                        {statsAssignments.quizzesSubmitted}
                        </span>
                    </td>
                    <td className="text-center">
                        <span className="progress-fraction">
                        {courseInfo?.quizzes?.length || 0}
                        </span>
                    </td>
                    <td className="text-center">
                        <span className="grade-display">
                        {(() => {
                            const quizzes = courseProgress?.quizProgress?.quizzes || [];
                            const graded = quizzes.filter(q => (q.bestGrade || 0) > 0);
                            if (graded.length === 0) return '0.0';
                            const sum = graded.reduce((s, q) => {
                                const maxGrade = courseInfo?.quizzes?.find(cq => cq.quizId === q.quizId)?.maxGrade || 1;
                                return s + (q.bestGrade / maxGrade) * 100;
                            }, 0);
                            return (sum / graded.length).toFixed(1);
                        })()}%
                        </span>
                    </td>
                    <td className="text-center">
                        <span className="grade-display">{Math.round((courseInfo?.quizzes?.reduce((sum, quiz) => sum + (quiz.gradeWeight || 0), 0) || 0) * 1000) / 10}%</span>
                    </td>
                    <td className="text-center">
                        <span className="grade-display">{courseGrade?.quizzesGrade?.toFixed(1) || '0.0'}%</span>
                    </td>
                    </tr>

                    {/* Exams Row */}
                    <tr>
                    <td>
                        <div className="student-info">
                        <span className="category-icon">📄</span>
                        <strong>Exams</strong>
                        </div>
                    </td>
                    <td className="text-center">
                        <span className="progress-fraction">
                        {statsAssignments.examsSubmitted}
                        </span>
                    </td>
                    <td className="text-center">
                        <span className="progress-fraction">
                        {courseInfo?.exams?.length || 0}
                        </span>
                    </td>
                    <td className="text-center">
                        <span className="grade-display">
                        {(() => {
                            const exams = courseProgress?.examProgress?.exams || [];
                            const graded = exams.filter(e => (e.finalGrade || 0) > 0);
                            if (graded.length === 0) return '0.0';
                            const sum = graded.reduce((s, e) => {
                                const maxGrade = courseInfo?.exams?.find(ce => ce.examId === e.examId)?.maxGrade || 1;
                                return s + (e.finalGrade / maxGrade) * 100;
                            }, 0);
                            return (sum / graded.length).toFixed(1);
                        })()}%
                        </span>
                    </td>
                    <td className="text-center">
                        <span className="grade-display">{Math.round((courseInfo?.exams?.reduce((sum, exam) => sum + (exam.gradeWeight || 0), 0) || 0) * 1000) / 10}%</span>
                    </td>
                    <td className="text-center">
                        <span className="grade-display">{courseGrade?.examsGrade?.toFixed(1) || '0.0'}%</span>
                    </td>
                    </tr>

                    {/* Capstones Row */}
                    <tr>
                    <td>
                        <div className="student-info">
                        <span className="category-icon">📘</span>
                        <strong>Capstones</strong>
                        </div>
                    </td>
                    <td className="text-center">
                        <span className="progress-fraction">
                        {statsAssignments.capstonesSubmitted}
                        </span>
                    </td>
                    <td className="text-center">
                        <span className="progress-fraction">
                        {courseInfo?.capstones?.length || 0}
                        </span>
                    </td>
                    <td className="text-center">
                        <span className="grade-display">
                        {(() => {
                            const capstones = courseProgress?.capstoneProjects?.capstones || [];
                            const graded = capstones.filter(c => (c.bestGrade || c.finalGrade || 0) > 0);
                            if (graded.length === 0) return '0.0';
                            const sum = graded.reduce((s, c) => {
                                const maxGrade = courseInfo?.capstones?.find(cc => cc.capstoneId === c.capstoneId)?.maxGrade || 1;
                                return s + ((c.bestGrade || c.finalGrade) / maxGrade) * 100;
                            }, 0);
                            return (sum / graded.length).toFixed(1);
                        })()}%
                        </span>
                    </td>
                    <td className="text-center">
                        <span className="grade-display">{Math.round((courseInfo?.capstones?.reduce((sum, cap) => sum + (cap.gradeWeight || 0), 0) || 0) * 1000) / 10}%</span>
                    </td>
                    <td className="text-center">
                        <span className="grade-display">{courseGrade?.capstoneGrade?.toFixed(1) || '0.0'}%</span>
                    </td>
                    </tr>

                    {/* Total Row */}
                    <tr className="total-row" style={{ backgroundColor: '#f0f9ff', fontWeight: 'bold' }}>
                    <td>
                        <div className="student-info">
                        <span className="category-icon">📊</span>
                        <strong>Overall Course Grade</strong>
                        </div>
                    </td>
                    <td className="text-center">
                        <span className="progress-fraction">
                        {statsAssignments.allSubmitted}
                        </span>
                    </td>
                    <td className="text-center">
                        <span className="progress-fraction">
                        {(courseInfo?.labs?.length || 0) + (courseInfo?.quizzes?.length || 0) + 
                        (courseInfo?.exams?.length || 0) + (courseInfo?.capstones?.length || 0)}
                        </span>
                    </td>
                    <td className="text-center">
                        <span className="grade-display" style={{ color: 'var(--m-text-muted)', fontSize: '12px' }}>—</span>
                    </td>
                    <td className="text-center">
                    <span className="grade-display">
                    {(() => {
                        let totalWeight = 0;
                        const submittedLabIds = courseProgress?.labProgress?.labs?.map(l => l.labId) || [];
                        const submittedQuizIds = courseProgress?.quizProgress?.quizzes?.map(q => q.quizId) || [];
                        const submittedExamIds = courseProgress?.examProgress?.exams?.map(e => e.examId) || [];
                        const submittedCapIds = courseProgress?.capstoneProjects?.capstones?.map(c => c.capstoneId) || [];
                        courseInfo?.labs?.filter(l => submittedLabIds.includes(l.labId)).forEach(l => { totalWeight += (l.gradeWeight || 0); });
                        courseInfo?.quizzes?.filter(q => submittedQuizIds.includes(q.quizId)).forEach(q => { totalWeight += (q.gradeWeight || 0); });
                        courseInfo?.exams?.filter(e => submittedExamIds.includes(e.examId)).forEach(e => { totalWeight += (e.gradeWeight || 0); });
                        courseInfo?.capstones?.filter(c => submittedCapIds.includes(c.capstoneId)).forEach(c => { totalWeight += (c.gradeWeight || 0); });
                        return Math.round(totalWeight * 1000) / 10;
                    })()}%
                    </span>
                    </td>
                    <td className="text-center">
                        <span className="grade-display" style={{ fontSize: '1.1em', color: '#2563eb' }}>
                        {(() => {
                            const finalGrade = courseGrade?.finalGrade || 0;
                            const isPending  = courseGrade?.letterGrade === 'P';
                            const passed     = !isPending && courseGrade?.letterGrade !== 'F';
                            return (
                                <>
                                    {finalGrade.toFixed(1)}%
                                    <span style={{ marginLeft: '8px', padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 700,
                                        background: isPending ? 'var(--m-amber-dim)' : passed ? 'var(--m-green-dim)' : 'var(--m-red-dim)',
                                        color:      isPending ? 'var(--m-amber)'     : passed ? 'var(--m-green)'     : 'var(--m-red)',
                                        border:     `1px solid ${isPending ? 'var(--m-amber-border)' : passed ? 'var(--m-green-border)' : 'var(--m-red-border)'}`
                                    }}>
                                        {isPending ? '⏳ Pending' : passed ? '✅ Pass' : '❌ Fail'}
                                    </span>
                                </>
                            );
                        })()}
                        </span>
                    </td>
                    </tr>
                </tbody>
                </table>
            </div>
            </div>
        );
    };

    return (
        <div className="page-container">
            {/* Header */}
            <div className="page-header">
                <ProfileHeader 
                    data = {authData}
                    title = {`📚 Course: ${courseInfo?.title} (${courseInfo?.courseId})`}
                    activePage = "studentdashboard" 
                />
                <div className="headerMiddle">
                    <img src="/images/prof_ix.png" alt="Logo" className="header-logo" />
                </div>
                <SharedHeader 
                    notificationCount={0} 
                    icons={['envelope']} 
                />
            </div>

            {/* Toolbar */}
            <div className="page-toolbar">
                <BackButton 
                    to="/student/dashboard"
                    activeTab="my-courses"
                    label="←"
                    bgColor="#6b7280"
                />
                <span className="page-toolbar-title-wrap">
                    <span className="page-toolbar-title">🗂️ Student Course Managment</span>
                    <span className="page-toolbar-subtitle">📚 Course: {courseInfo?.title} ({courseInfo?.courseId})</span>
                </span>
            </div>

            {/* Tab Navigation */}
            <div className="page-top-tabs">
                
                <button
                    className={`tab-button ${activeTab === 'content' ? 'active-tab' : ''}`}
                    onClick={() => setActiveTab('content')}
                >
                    📊 Contents
                </button>

                <button
                    className={`tab-button ${activeTab === 'progress' ? 'active-tab' : ''}`}
                    onClick={() => setActiveTab('progress')}
                >
                    📚 Progress 
                </button>

                <button
                    className={`tab-button ${activeTab === 'communication' ? 'active-tab' : ''}`}
                    onClick={() => setActiveTab('communication')}
                >
                    💬 Communication
                </button>

            </div>

            {/* All Content Area */}
            <div className="page-content">
                {isLoading ? (
                <div className="loading-container">
                    <div className="loader"></div>
                    <p>Loading course content...</p>
                </div>
                ) : (
                <>
                    {/* Content Tab */}
                    {activeTab === 'content' && (
                    <div className="page-inner-wrap">
                        {/* ── Content Navigation ── */}
                        <div className="acd-section" style={{ marginBottom: '16px' }}>
                        <div className="acd-section-title">Course Content</div>
                        <div className="acd-content-grid">
                            <button className="acd-content-btn" onClick={() => openListModal('lessons')}>
                            📚 <span>Lessons</span> <span className="acd-content-count">({courseInfo?.lessons?.length || 0})</span>
                            </button>
                            <button className="acd-content-btn" onClick={() => openListModal('labs')}>
                            🧪 <span>Labs</span> <span className="acd-content-count">({courseInfo?.labs?.length || 0})</span>
                            </button>
                            <button className="acd-content-btn" onClick={() => openListModal('quizzes')}>
                            📋 <span>Quizzes</span> <span className="acd-content-count">({courseInfo?.quizzes?.length || 0})</span>
                            </button>
                            <button className="acd-content-btn" onClick={() => openListModal('exams')}>
                            📄 <span>Exams</span> <span className="acd-content-count">({courseInfo?.exams?.length || 0})</span>
                            </button>
                            <button className="acd-content-btn" onClick={() => openListModal('capstones')}>
                            📘 <span>Capstones</span> <span className="acd-content-count">({courseInfo?.capstones?.length || 0})</span>
                            </button>
                            <button className="acd-content-btn" 
                                style={{backgroundColor: 'aqua', width: 'fit-content'}}
                                onClick={() => 
                                    navigate(`/labengine/course/${courseInfo.courseId}/canvases`)
                                }
                            >
                                {CourseDomains[courseInfo.category]?.buttonTitle || '🔧 My Circuits'}
                                <span className="acd-content-count">({myCircuitsCount})</span>
                            </button>

                        </div>
                        </div>


                        {/* ── Course Overview ── */}
                        <div className="acd-section" style={{ marginBottom: '16px' }}>
                        <div className="acd-section-title-row">
                            <span className="acd-section-title">📖 Course Information</span>
                            {hasTextbook && (
                            <a
                                href={`/courses/${courseInfo.category}/index.html`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="acd-textbook-link"
                            >
                                <img src="/images/prof_ix.png" alt="Prof-iX" className="acd-textbook-img" />
                                📚 Course eBook
                            </a>
                            )}
                        </div>

                        <div className="acd-meta-grid">
                            <div className="acd-meta-item">
                            <img
                                src={courseInfo?.instructorImageUrl || '/images/default-avatar.png'}
                                alt={courseInfo?.instructorName}
                                className="acd-meta-avatar"
                            />
                            <div className="acd-meta-content">
                                <span className="acd-meta-label">Instructor</span>
                                <span className="acd-meta-value">{courseInfo?.instructorName}</span>
                            </div>
                            </div>
                            <div className="acd-meta-item">
                            <span className="acd-meta-icon">📧</span>
                            <div className="acd-meta-content">
                                <span className="acd-meta-label">Instructor Email</span>
                                <span className="acd-meta-value">{courseInfo?.instructorEmail}</span>
                            </div>
                            </div>
                            <div className="acd-meta-item">
                            <span className="acd-meta-icon">📊</span>
                            <div className="acd-meta-content">
                                <span className="acd-meta-label">Level</span>
                                <span className="acd-meta-value">{courseInfo?.level}</span>
                            </div>
                            </div>
                            <div className="acd-meta-item">
                            <span className="acd-meta-icon">⏱️</span>
                            <div className="acd-meta-content">
                                <span className="acd-meta-label">Duration</span>
                                <span className="acd-meta-value">{courseInfo?.duration}</span>
                            </div>
                            </div>
                            <div className="acd-meta-item">
                            <span className="acd-meta-icon">🎓</span>
                            <div className="acd-meta-content">
                                <span className="acd-meta-label">Credits</span>
                                <span className="acd-meta-value">{courseInfo?.credits}</span>
                            </div>
                            </div>
                            <div className="acd-meta-item">
                            <span className="acd-meta-icon">🎯</span>
                            <div className="acd-meta-content">
                                <span className="acd-meta-label">Passing Grade</span>
                                <span className="acd-meta-value">{courseInfo?.passingGrade}</span>
                            </div>
                            </div>
                            <div className="acd-meta-item">
                            <span className="acd-meta-icon">📂</span>
                            <div className="acd-meta-content">
                                <span className="acd-meta-label">Category</span>
                                <span className="acd-meta-value">{courseInfo?.category}</span>
                            </div>
                            </div>
                            <div className="acd-meta-item">
                            <span className="acd-meta-icon">👥</span>
                            <div className="acd-meta-content">
                                <span className="acd-meta-label">Enrolled Students</span>
                                <span className="acd-meta-value">{courseInfo?.enrolledStudents}</span>
                            </div>
                            </div>
                            <div className="acd-meta-item">
                            <span className="acd-meta-icon">🔄</span>
                            <div className="acd-meta-content">
                                <span className="acd-meta-label">Last Updated</span>
                                <span className="acd-meta-value">{new Date(courseInfo?.updatedAt).toLocaleDateString()}</span>
                            </div>
                            </div>
                        </div>

                        <div className="acd-description">
                            <p className="acd-short-desc">{courseInfo?.shortDescription}</p>
                            <p className="acd-long-desc">{courseInfo?.longDescription}</p>
                        </div>
                        </div>

                        {/* ── Prerequisites ── */}
                        <div className="acd-section" style={{ marginBottom: '16px' }}>
                        <div className="acd-section-title">📱 Prerequisites</div>
                        <div className="acd-tags">
                            {courseInfo?.prerequisites?.length > 0
                            ? courseInfo.prerequisites.map((pre, idx) => (
                                <span key={idx} className="acd-tag acd-tag--prereq">{pre}</span>
                                ))
                            : <p className="acd-empty-msg">No prerequisites required</p>
                            }
                        </div>
                        </div>

                        {/* ── Tags ── */}
                        <div className="acd-section" style={{ marginBottom: '16px' }}>
                        <div className="acd-section-title">💡 Course Tags</div>
                        <div className="acd-tags">
                            {tags?.length > 0
                            ? tags.map((tag, idx) => (
                                <span key={idx} className="acd-tag acd-tag--course">{tag}</span>
                                ))
                            : <p className="acd-empty-msg">No tags available</p>
                            }
                        </div>
                        </div>

                        {/* ── Grading Scheme ── */}
                        <div className="acd-section">
                            <div className="acd-section-title">📊 Grading Scheme</div>

                            {courseInfo?.labs?.length > 0 && (
                                <div className="acd-grade-category">
                                <button className="acd-grade-category-hdr" onClick={() => setGradingCollapsed(p => ({ ...p, labs: !p.labs }))} style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center' }}>
                                    🧪 Labs
                                    <span className="acd-grade-total">{Math.round(courseInfo.labs.reduce((s, l) => s + (l.gradeWeight || 0), 0) * 1000) / 10}%</span>
                                    <span style={{ marginLeft: 'auto' }}>{gradingCollapsed['labs'] ? '▼' : '▲'}</span>
                                </button>
                                {!gradingCollapsed['labs'] && courseInfo.labs.map((lab, idx) => (
                                    <div key={idx} className="acd-weight-row">
                                    <div className="acd-weight-info">
                                        <span className="acd-weight-id">{lab.labId}</span>
                                        <span className="acd-weight-title">{lab.title}</span>
                                    </div>
                                    <div className="acd-weight-display">
                                        <span className="acd-weight-val">{Math.round((lab.gradeWeight || 0) * 1000) / 10}%</span>
                                    </div>
                                    </div>
                                ))}
                                </div>
                            )}

                            {courseInfo?.quizzes?.length > 0 && (
                                <div className="acd-grade-category">
                                <button className="acd-grade-category-hdr" onClick={() => setGradingCollapsed(p => ({ ...p, quizzes: !p.quizzes }))} style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center' }}>
                                    📋 Quizzes
                                    <span className="acd-grade-total">{Math.round(courseInfo.quizzes.reduce((s, q) => s + (q.gradeWeight || 0), 0) * 1000) / 10}%</span>
                                    <span style={{ marginLeft: 'auto' }}>{gradingCollapsed['quizzes'] ? '▼' : '▲'}</span>
                                </button>
                                {!gradingCollapsed['quizzes'] && courseInfo.quizzes.map((quiz, idx) => (
                                    <div key={idx} className="acd-weight-row">
                                    <div className="acd-weight-info">
                                        <span className="acd-weight-id">{quiz.quizId}</span>
                                        <span className="acd-weight-title">{quiz.title}</span>
                                    </div>
                                    <div className="acd-weight-display">
                                        <span className="acd-weight-val">{Math.round((quiz.gradeWeight || 0) * 1000) / 10}%</span>
                                    </div>
                                    </div>
                                ))}
                                </div>
                            )}

                            {courseInfo?.exams?.length > 0 && (
                                <div className="acd-grade-category">
                                <button className="acd-grade-category-hdr" onClick={() => setGradingCollapsed(p => ({ ...p, exams: !p.exams }))} style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center' }}>
                                    📄 Exams
                                    <span className="acd-grade-total">{Math.round(courseInfo.exams.reduce((s, e) => s + (e.gradeWeight || 0), 0) * 1000) / 10}%</span>
                                    <span style={{ marginLeft: 'auto' }}>{gradingCollapsed['exams'] ? '▼' : '▲'}</span>
                                </button>
                                {!gradingCollapsed['exams'] && courseInfo.exams.map((exam, idx) => (
                                    <div key={idx} className="acd-weight-row">
                                    <div className="acd-weight-info">
                                        <span className="acd-weight-id">{exam.examId}</span>
                                        <span className="acd-weight-title">{exam.title}</span>
                                    </div>
                                    <div className="acd-weight-display">
                                        <span className="acd-weight-val">{Math.round((exam.gradeWeight || 0) * 1000) / 10}%</span>
                                    </div>
                                    </div>
                                ))}
                                </div>
                            )}

                            {courseInfo?.capstones?.length > 0 && (
                                <div className="acd-grade-category">
                                <button className="acd-grade-category-hdr" onClick={() => setGradingCollapsed(p => ({ ...p, capstones: !p.capstones }))} style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'center' }}>
                                    📘 Capstones
                                    <span className="acd-grade-total">{Math.round(courseInfo.capstones.reduce((s, c) => s + (c.gradeWeight || 0), 0) * 1000) / 10}%</span>
                                    <span style={{ marginLeft: 'auto' }}>{gradingCollapsed['capstones'] ? '▼' : '▲'}</span>
                                </button>
                                {!gradingCollapsed['capstones'] && courseInfo.capstones.map((cap, idx) => (
                                    <div key={idx} className="acd-weight-row">
                                    <div className="acd-weight-info">
                                        <span className="acd-weight-id">{cap.capstoneId}</span>
                                        <span className="acd-weight-title">{cap.title}</span>
                                    </div>
                                    <div className="acd-weight-display">
                                        <span className="acd-weight-val">{Math.round((cap.gradeWeight || 0) * 1000) / 10}%</span>
                                    </div>
                                    </div>
                                ))}
                                </div>
                            )}

                            {/* Total */}
                            {(() => {
                                let total = 0;
                                courseInfo?.labs?.forEach(l => { total += (l.gradeWeight || 0) * 100; });
                                courseInfo?.quizzes?.forEach(q => { total += (q.gradeWeight || 0) * 100; });
                                courseInfo?.exams?.forEach(e => { total += (e.gradeWeight || 0) * 100; });
                                courseInfo?.capstones?.forEach(c => { total += (c.gradeWeight || 0) * 100; });
                                const rounded = Math.round(total * 10) / 10;
                                const valid = Math.abs(rounded - 100) < 0.5;
                                return (
                                <div className={`acd-weight-summary ${valid ? 'acd-weight-summary--valid' : 'acd-weight-summary--invalid'}`}>
                                    <span className="acd-weight-summary-label">Total Course Weight</span>
                                    <span className="acd-weight-summary-value">{rounded}% {valid ? '✓' : '⚠'}</span>
                                </div>
                                );
                            })()}
                        </div>

                    </div>
                    )}

                    {activeTab === 'progress' && (
                    <div className="page-inner-wrap">
                        {/* Statistics Cards */}
                        <div className="stats-grid">
                            <div className="stat-card">
                                <div className="stat-icon">📊</div>
                                <div className="stat-info">
                                <h3>{statsAssignments.allPending} | {statsAssignments.allSubmitted}</h3>
                                <p>Pending/Total Assignments</p>
                                </div>
                            </div>
                        </div>

                        {/* Labs Progress Table */}
                        {renderStudentProgressTable(courseInfo?.labs, 'lab', '🧪', 'Labs')}

                        {/* Quizzes Progress Table */}
                        {renderStudentProgressTable(courseInfo?.quizzes, 'quiz', '📋', 'Quizzes')}

                        {/* Exams Progress Table */}
                        {renderStudentProgressTable(courseInfo?.exams, 'exam', '📄', 'Exams')}

                        {/* Capstones Progress Table */}
                        {renderStudentProgressTable(courseInfo?.capstones, 'capstone', '📘', 'Capstone Projects')}                       

                        {/* Overall Statistics */}
                        {renderOverallStatistics()}
                    </div>
                    )}

                    {/* Messages Tab */}
                    {activeTab === 'communication' && (
                    <ConversationModal
                        role="student"
                        messages={conversationMessages}
                        isLoading={false}
                        recipient={instructorInfo}
                        currentUserId={authData?.id}
                        messageText={messageText}
                        onMessageTextChange={setMessageText}
                        onSendMessage={handleSendMessage}
                        studentAnnouncements={studentAnnouncements}
                        onViewAnnouncement={(ann) => { setSelectedAnnouncement(ann); setAnnouncementModalVisible(true); }}
                    />
                    )}

                    {announcementModalVisible && selectedAnnouncement && (
                    <AnnouncementDetailModal
                        visible={announcementModalVisible}
                        onClose={() => setAnnouncementModalVisible(false)}
                        announcement={selectedAnnouncement}
                    />
                    )}
                    
                </>
                )}
            </div>

            {/* ====================================================================== */}
            {/* STUDENT COURSE DETAIL MODAL */}
            {/* ====================================================================== */} 
            {/* Student Content List Modal */}
            <ContentListModal
                visible={showListModal && !!selectedListType}
                onClose={closeListModal}
                role="student"
                contentType={selectedListType}
                items={courseInfo?.[selectedListType] || []}
                onView={(itemId) => openContentModalFromList(selectedListType, itemId, 'overview')}
            />

            {/* Student LESSON MODAL */}
            <LessonModal
                visible={showContentModal && selectedContentType === 'lessons'}
                onClose={closeContentModal}
                role="student"
                lesson={(() => {
                    return courseInfo?.lessons?.find(l => l.lessonId === selectedItemId) || null;
                })()}
                authData={authData}
                courseId={courseId}
                onLoad={loadPage}
                onDownload={handleDownloadAttachment}
            />

            {/* STUDENT ASSESSMENT MODAL — overview phase (lab/quiz/exam detail view) */}
            <AssessmentModal
                visible={showContentModal && (selectedContentType === 'labs' || selectedContentType === 'quizzes' || selectedContentType === 'exams' || selectedContentType === 'capstones')}
                authData={authData}
                courseId={courseId}
                domain={domainName}
                role="student"
                phase={assessmentPhase}
                assessmentType={
                    selectedContentType === 'labs' ? 'lab' :
                    selectedContentType === 'quizzes' ? 'quiz' : 'exam'
                }
                content={(() => {
                    if (!selectedContentType || !selectedItemId) return null;
                    const items = courseInfo?.[selectedContentType];
                    const idField = selectedContentType === 'labs' ? 'labId' :
                    selectedContentType === 'quizzes' ? 'quizId' : 'examId';
                    return items?.find(i => i[idField] === selectedItemId) || null;
                })()}
                labSessions={labSimulators}
                onClose={closeContentModal}
                labGradingModals={iotModalOpen}
                setLabGradingModals={setLabModalOpen}
                onDownload={handleDownloadAttachment}
                courseProgress={courseProgress}
                courseCircuits={courseCircuits}
                studentName={authData.name}
                studentLoginId={authData.id}
                onAfterSubmit={() => { loadPage(); }}
            />

            {courseInfo && (
            <ProfIXChatbot 
                userName={authData.name}
                courseId={courseId} 
                courseName={courseInfo.title} 
                category={courseInfo.category}
            />
            )}

        </div>
    );
};

export default StudentCourseManagement;
                    